import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Search, Filter, Heart, MapPin, GraduationCap, Calendar, DollarSign, Users } from 'lucide-react';
import { useGetAvailableChildrenQuery } from '../../store/slices/apiSlice';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import toast from 'react-hot-toast';
import { usePageMetadata } from '../../hooks/usePageMetadata';
import Navbar from '../../components/common/Navbar';

const BrowseChildren: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [ageMin, setAgeMin] = useState('');
  const [ageMax, setAgeMax] = useState('');
  const [location, setLocation] = useState('');
  const [educationStage, setEducationStage] = useState('');
  const [filterCurrency, setFilterCurrency] = useState(false);
  const itemsPerPage = 12;

  usePageMetadata('sponsor-a-child');

  const { data: childrenResponse, isLoading } = useGetAvailableChildrenQuery({
    page: currentPage,
    limit: itemsPerPage,
    age_min: ageMin ? Number(ageMin) : undefined,
    age_max: ageMax ? Number(ageMax) : undefined,
    location: location || undefined,
    education_stage: educationStage || undefined,
    filter_currency: filterCurrency,
  }, {
    skip: !user,
  });

  const children = childrenResponse?.data?.children || [];
  const pagination = childrenResponse?.data?.pagination || {
    current_page: 1,
    total_pages: 1,
    total_children: 0,
    limit: itemsPerPage,
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [ageMin, ageMax, location, educationStage, filterCurrency, searchTerm]);

  const filteredChildren = children.filter((child) => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      child.name.toLowerCase().includes(searchLower) ||
      child.location.toLowerCase().includes(searchLower) ||
      child.education_stage.toLowerCase().includes(searchLower) ||
      child.bio.toLowerCase().includes(searchLower)
    );
  });

  const formatCurrency = (amount: number, symbol: string) => {
    return `${symbol}${amount.toLocaleString('en-US')}`;
  };

  if (!user) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <Card className="p-12 text-center">
              <p className="text-gray-600 mb-4">Please login to browse children available for sponsorship.</p>
              <Button onClick={() => window.location.href = 'https://app.diasporawise.com'} className="lg:hidden">Login</Button>
            </Card>
          </div>
        </div>
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 py-8 px-4">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          {/* Top Action Buttons */}
          <div className="flex items-center justify-between mb-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </Button>
            <Button
              onClick={() => navigate('/sponsor/my-children')}
              className="flex items-center gap-2 bg-gray-800 hover:bg-gray-900 text-white"
            >
              <Heart className="w-4 h-4" />
              My Sponsored Children
            </Button>
          </div>

          <div className="mb-4">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Sponsor a Child</h1>
            <p className="text-gray-600">Make a difference in a child's life through education sponsorship</p>
          </div>
        </div>

        {/* Filters */}
        <Card className="p-4 mb-6">
          <div className="space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search by name, location, or education stage..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              />
            </div>

            {/* Filter Row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Age Range */}
              <div className="flex gap-2">
                <input
                  type="number"
                  placeholder="Min Age"
                  value={ageMin}
                  onChange={(e) => setAgeMin(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  min="0"
                />
                <input
                  type="number"
                  placeholder="Max Age"
                  value={ageMax}
                  onChange={(e) => setAgeMax(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
                  min="0"
                />
              </div>

              {/* Location */}
              <input
                type="text"
                placeholder="Location"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              />

              {/* Education Stage */}
              <input
                type="text"
                placeholder="Education Stage"
                value={educationStage}
                onChange={(e) => setEducationStage(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-transparent"
              />

              {/* Currency Filter */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={filterCurrency}
                  onChange={(e) => setFilterCurrency(e.target.checked)}
                  className="w-4 h-4 text-gray-800 border-gray-300 rounded focus:ring-gray-500"
                />
                <span className="text-sm text-gray-700">Match My Currency</span>
              </label>

              {/* Clear Filters */}
              {(ageMin || ageMax || location || educationStage || filterCurrency) && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setAgeMin('');
                    setAgeMax('');
                    setLocation('');
                    setEducationStage('');
                    setFilterCurrency(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <Filter className="w-4 h-4" />
                  Clear
                </Button>
              )}
            </div>
          </div>
        </Card>

        {/* Results Count */}
        <div className="mb-4">
          <p className="text-sm text-gray-600">
            Showing {filteredChildren.length} of {pagination.total_children} {pagination.total_children === 1 ? 'child' : 'children'}
            {pagination.total_pages > 1 && ` (Page ${pagination.current_page} of ${pagination.total_pages})`}
          </p>
        </div>

        {/* Children Grid */}
        {filteredChildren.length === 0 ? (
          <Card className="p-12 text-center">
            <Heart className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Children Found</h3>
            <p className="text-gray-600">
              {searchTerm || ageMin || ageMax || location || educationStage
                ? 'Try adjusting your search criteria'
                : 'No children available for sponsorship at the moment'}
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredChildren.map((child) => (
              <Link
                key={child.child_id}
                to={`/sponsor/child/${child.child_id}`}
                className="block"
              >
                <Card className="overflow-hidden hover:shadow-lg transition-shadow h-full flex flex-col">
                  {/* Image */}
                  <div className="relative h-64 bg-gray-200">
                    <img
                      src={child.profile_image || 'https://via.placeholder.com/400x300?text=No+Image'}
                      alt={child.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400x300?text=No+Image';
                      }}
                    />
                    {child.is_verified && (
                      <div className="absolute top-3 right-3">
                        <Badge variant="success" className="bg-green-500 text-white">
                          Verified
                        </Badge>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  <div className="p-4 flex-1 flex flex-col">
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
                      {child.name}
                    </h3>
                    <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                      {child.bio}
                    </p>

                    {/* Details */}
                    <div className="space-y-2 mb-4 flex-1">
                      <div className="flex items-center text-sm text-gray-600">
                        <MapPin className="w-4 h-4 mr-2" />
                        <span className="truncate">{child.location}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <GraduationCap className="w-4 h-4 mr-2" />
                        <span>{child.education_stage}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <Users className="w-4 h-4 mr-2" />
                        <span>Age: {child.age} years</span>
                      </div>
                    </div>

                    {/* Funding Options */}
                    <div className="border-t border-gray-200 pt-3 mb-4">
                      <p className="text-xs font-medium text-gray-600 mb-2">Funding Options:</p>
                      <div className="space-y-1">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Weekly:</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(child.funding_options.weekly, child.currency_symbol)}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Monthly:</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(child.funding_options.monthly, child.currency_symbol)}
                          </span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-600">Yearly:</span>
                          <span className="font-semibold text-gray-900">
                            {formatCurrency(child.funding_options.yearly, child.currency_symbol)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Sponsor Button */}
                    <Button className="w-full bg-gray-800 hover:bg-gray-900 flex items-center justify-center gap-2">
                      <Heart className="w-4 h-4" />
                      Sponsor Now
                    </Button>
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="mt-8 flex items-center justify-center gap-2">
            <Button
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
            >
              Previous
            </Button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
                let pageNum: number;
                if (pagination.total_pages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= pagination.total_pages - 2) {
                  pageNum = pagination.total_pages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 rounded-lg font-medium transition-colors ${
                      currentPage === pageNum
                        ? 'bg-gray-800 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <Button
              variant="outline"
              onClick={() => setCurrentPage((prev) => Math.min(pagination.total_pages, prev + 1))}
              disabled={currentPage === pagination.total_pages}
            >
              Next
            </Button>
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default BrowseChildren;
